import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import api from '../../services/api';
import type {
  AccountTrendFocus,
  AccountTrendGranularity,
  AccountTrendMetric,
  AccountTrendResponse
} from '../../types/accountTrend';
import {
  focusAnchorFromBucket,
  getTodayAnchor,
  normalizeFocusAnchor,
  shiftFocusAnchor
} from '../../utils/accountDashboardFocus';

const METRIC_STORAGE_PREFIX = 'companyTrendMetric:';

function readStoredMetric(companyId: string): AccountTrendMetric | null {
  try {
    const raw = sessionStorage.getItem(`${METRIC_STORAGE_PREFIX}${companyId}`);
    if (raw === 'hours' || raw === 'internal_cost' || raw === 'billable') {
      return raw;
    }
  } catch {
    // ignore
  }
  return null;
}

function writeStoredMetric(companyId: string, metric: AccountTrendMetric): void {
  try {
    sessionStorage.setItem(`${METRIC_STORAGE_PREFIX}${companyId}`, metric);
  } catch {
    // ignore
  }
}

export interface CompanyDashboardContextValue {
  companyId: string;
  accessGranted: boolean | null;
  metric: AccountTrendMetric;
  granularity: AccountTrendGranularity;
  anchor: string;
  focus: AccountTrendFocus | null;
  focusWindow: { from: string; to: string; label: string } | null;
  trendData: AccountTrendResponse | null;
  trendLoading: boolean;
  trendRefreshing: boolean;
  trendError: string | null;
  currencyCode: string;
  companyTimezone: string;
  currencyWarning: boolean;
  setMetric: (metric: AccountTrendMetric) => void;
  setGranularity: (granularity: AccountTrendGranularity) => void;
  goPrev: () => void;
  goNext: () => void;
  setFocusFromBucket: (bucket: { key: string; from: string; to: string }) => void;
  refreshAccess: () => Promise<void>;
}

const CompanyDashboardContext = createContext<CompanyDashboardContextValue | null>(null);

export function useCompanyDashboard(): CompanyDashboardContextValue {
  const ctx = useContext(CompanyDashboardContext);
  if (!ctx) {
    throw new Error('useCompanyDashboard must be used within CompanyDashboardProvider');
  }
  return ctx;
}

export function useCompanyDashboardOptional(): CompanyDashboardContextValue | null {
  return useContext(CompanyDashboardContext);
}

interface CompanyDashboardProviderProps {
  companyId: string;
  companyCurrency: string;
  children: React.ReactNode;
}

export function CompanyDashboardProvider({
  companyId,
  companyCurrency,
  children
}: CompanyDashboardProviderProps) {
  const [accessGranted, setAccessGranted] = useState<boolean | null>(null);
  const [metric, setMetricState] = useState<AccountTrendMetric>('hours');
  const [granularity, setGranularityState] = useState<AccountTrendGranularity>('month');
  const [anchor, setAnchor] = useState(() => normalizeFocusAnchor(getTodayAnchor(), 'month'));
  const [trendData, setTrendData] = useState<AccountTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendRefreshing, setTrendRefreshing] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  const trendRequestRef = useRef(0);
  const trendDataRef = useRef<AccountTrendResponse | null>(null);
  trendDataRef.current = trendData;

  const refreshAccess = useCallback(async () => {
    try {
      setTrendError(null);
      const res = await api.getCompanyBudgetProgress(companyId);
      if (res.success) {
        setAccessGranted(true);
        const stored = readStoredMetric(companyId);
        setMetricState(stored ?? 'hours');
        if (!stored) {
          writeStoredMetric(companyId, 'hours');
        }
      } else {
        setAccessGranted(false);
      }
    } catch (err: unknown) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setAccessGranted(false);
        return;
      }
      setAccessGranted(false);
    }
  }, [companyId]);

  useEffect(() => {
    setAccessGranted(null);
    setTrendData(null);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), 'month'));
    refreshAccess();
  }, [companyId, refreshAccess]);

  const setMetric = useCallback(
    (next: AccountTrendMetric) => {
      setMetricState(next);
      writeStoredMetric(companyId, next);
    },
    [companyId]
  );

  const setGranularity = useCallback((next: AccountTrendGranularity) => {
    setGranularityState(next);
    setAnchor(normalizeFocusAnchor(getTodayAnchor(), next));
  }, []);

  const goPrev = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, -1));
  }, [granularity]);

  const goNext = useCallback(() => {
    setAnchor((prev) => shiftFocusAnchor(prev, granularity, 1));
  }, [granularity]);

  const setFocusFromBucket = useCallback(
    (bucket: { key: string; from: string; to: string }) => {
      setAnchor(focusAnchorFromBucket(bucket.from, granularity));
    },
    [granularity]
  );

  const loadTrend = useCallback(async () => {
    if (!accessGranted) {
      setTrendLoading(false);
      setTrendRefreshing(false);
      return;
    }

    const requestId = ++trendRequestRef.current;
    const hasExistingData = trendDataRef.current !== null;

    try {
      if (hasExistingData) {
        setTrendRefreshing(true);
      } else {
        setTrendLoading(true);
      }
      setTrendError(null);

      const res = await api.getCompanyTrend(companyId, { metric, granularity, anchor });
      if (requestId !== trendRequestRef.current) {
        return;
      }

      if (res.success) {
        setTrendData(res.data as AccountTrendResponse);
      } else {
        setTrendData(null);
        setTrendError(res.message ?? 'Failed to load company trend');
      }
    } catch (err: unknown) {
      if (requestId !== trendRequestRef.current) {
        return;
      }
      const status =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 403) {
        setAccessGranted(false);
        setTrendData(null);
        return;
      }
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setTrendData(null);
      setTrendError(message ?? 'Failed to load company trend');
    } finally {
      if (requestId === trendRequestRef.current) {
        setTrendLoading(false);
        setTrendRefreshing(false);
      }
    }
  }, [accessGranted, companyId, metric, granularity, anchor]);

  useEffect(() => {
    loadTrend();
  }, [loadTrend]);

  const focus = trendData?.focus ?? null;

  const focusWindow = useMemo(() => {
    if (!focus) return null;
    return {
      from: focus.from,
      to: focus.to,
      label: focus.label
    };
  }, [focus]);

  const currencyCode = trendData?.meta.currencyCode ?? companyCurrency;
  const companyTimezone = trendData?.meta.timezone ?? 'UTC';
  const currencyWarning = Boolean(trendData?.meta.currencyWarning);

  const value = useMemo(
    (): CompanyDashboardContextValue => ({
      companyId,
      accessGranted,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      companyTimezone,
      currencyWarning,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshAccess
    }),
    [
      companyId,
      accessGranted,
      metric,
      granularity,
      anchor,
      focus,
      focusWindow,
      trendData,
      trendLoading,
      trendRefreshing,
      trendError,
      currencyCode,
      companyTimezone,
      currencyWarning,
      setMetric,
      setGranularity,
      goPrev,
      goNext,
      setFocusFromBucket,
      refreshAccess
    ]
  );

  return (
    <CompanyDashboardContext.Provider value={value}>{children}</CompanyDashboardContext.Provider>
  );
}
